import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Podświetlenie miejsca w serwisie (samouczek „Pierwsze kroki”): przyciemnia stronę wokół elementu
 * z `data-samouczek="<cel>"`, obrysowuje go i pokazuje dymek z krótką podpowiedzią. Sam element zostaje klikalny
 * (przyciemnienie to cztery prostokąty wokół niego), a klik w niego, w przyciemnienie, „Rozumiem” albo Escape
 * zamyka podświetlenie. Element szukamy kilka sekund, bo po przejściu na inną stronę dopiero się wczytuje;
 * gdy się nie pojawi (np. brak uprawnień), podświetlenie znika bez komunikatu, a panel dalej mówi, gdzie szukać.
 */

const MARGINES = 8
const SZUKAJ_MS = 6000
const SZEROKOSC_DYMKU = 340

const Podswietlenie = ({ cel, dymek, onZamknij }) => {
  const [ramka, setRamka] = useState(null)
  const element = useRef(null)
  const przycisk = useRef(null)
  const zamknij = useRef(onZamknij)
  zamknij.current = onZamknij

  useEffect(() => {
    let koniec = false
    let szukanie = null
    let zegar = null
    const start = Date.now()
    const odswiez = () => {
      if (!element.current) return
      const r = element.current.getBoundingClientRect()
      setRamka((s) => (s && s.top === r.top && s.left === r.left && s.width === r.width && s.height === r.height
        ? s : { top: r.top, left: r.left, width: r.width, height: r.height }))
    }
    const klikWCel = () => setTimeout(() => zamknij.current?.(), 0)
    const znajdz = () => {
      if (koniec) return
      const el = document.querySelector(`[data-samouczek="${cel}"]`)
      if (!el) {
        if (Date.now() - start < SZUKAJ_MS) szukanie = setTimeout(znajdz, 150)
        else zamknij.current?.()
        return
      }
      element.current = el
      el.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
      el.addEventListener('click', klikWCel, true)
      odswiez()
      // Położenie śledzimy przy przewijaniu i zmianie okna, a zegar łapie przesunięcia od doczytanych treści.
      window.addEventListener('scroll', odswiez, true)
      window.addEventListener('resize', odswiez)
      zegar = setInterval(odswiez, 250)
    }
    znajdz()
    const escape = (e) => { if (e.key === 'Escape') zamknij.current?.() }
    document.addEventListener('keydown', escape)
    return () => {
      koniec = true
      clearTimeout(szukanie)
      clearInterval(zegar)
      element.current?.removeEventListener('click', klikWCel, true)
      element.current = null
      window.removeEventListener('scroll', odswiez, true)
      window.removeEventListener('resize', odswiez)
      document.removeEventListener('keydown', escape)
    }
  }, [cel])

  useEffect(() => { if (ramka) przycisk.current?.focus({ preventScroll: true }) }, [Boolean(ramka)]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!ramka) return null

  const vw = window.innerWidth
  const vh = window.innerHeight
  // Otwór przycięty do okna: wysoka sekcja nie wypycha dymku poza ekran.
  const gora = Math.max(0, ramka.top - MARGINES)
  const lewo = Math.max(0, ramka.left - MARGINES)
  const dol = Math.min(vh, ramka.top + ramka.height + MARGINES)
  const prawo = Math.min(vw, ramka.left + ramka.width + MARGINES)
  const szer = Math.min(SZEROKOSC_DYMKU, vw - 32)
  const miejscePod = vh - dol
  const miejsceNad = gora
  const pod = miejscePod >= 190 || miejscePod >= miejsceNad
  const dymekLewo = Math.min(Math.max(16, (lewo + prawo) / 2 - szer / 2), vw - szer - 16)
  const dymekStyl = pod
    ? { top: Math.min(dol + 12, vh - 150), left: dymekLewo, width: szer }
    : { bottom: Math.min(vh - gora + 12, vh - 150), left: dymekLewo, width: szer }
  const strzalka = Math.min(Math.max(20, (lewo + prawo) / 2 - dymekLewo), szer - 20)
  // Przyciemnienie rysuje cień wokół otworu (pewniejsze niż półprzezroczyste tła); cztery przezroczyste
  // prostokąty wokół otworu tylko łapią kliknięcia, które zamykają podświetlenie.
  const zaslona = 'absolute pointer-events-auto'
  const zamykaj = () => onZamknij?.()

  return createPortal(
    <div className="pointer-events-none fixed inset-0 z-[60] print:hidden" data-testid="podswietlenie">
      <div className={zaslona} style={{ top: 0, left: 0, right: 0, height: gora }} onClick={zamykaj} aria-hidden="true" />
      <div className={zaslona} style={{ top: dol, left: 0, right: 0, bottom: 0 }} onClick={zamykaj} aria-hidden="true" />
      <div className={zaslona} style={{ top: gora, left: 0, width: lewo, height: dol - gora }} onClick={zamykaj} aria-hidden="true" />
      <div className={zaslona} style={{ top: gora, left: prawo, right: 0, height: dol - gora }} onClick={zamykaj} aria-hidden="true" />
      <div aria-hidden="true" className="absolute rounded-xl"
           style={{ top: gora, left: lewo, width: prawo - lewo, height: dol - gora, boxShadow: '0 0 0 200vmax rgba(15, 23, 42, 0.55)' }} />
      <div aria-hidden="true" className="absolute rounded-xl ring-4 ring-blue-500"
           style={{ top: gora, left: lewo, width: prawo - lewo, height: dol - gora }} />
      <div aria-hidden="true" className="absolute animate-pulse rounded-2xl ring-2 ring-blue-300"
           style={{ top: gora - 6, left: lewo - 6, width: prawo - lewo + 12, height: dol - gora + 12 }} />
      <div role="dialog" aria-labelledby="samouczek-dymek-tytul" aria-describedby="samouczek-dymek-tekst"
           className="pointer-events-auto absolute rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl" style={dymekStyl}>
        <span aria-hidden="true" className={`absolute h-3 w-3 rotate-45 border-gray-200 bg-white ${pod ? '-top-1.5 border-l border-t' : '-bottom-1.5 border-b border-r'}`}
              style={{ left: strzalka - 6 }} />
        <p id="samouczek-dymek-tytul" className="text-sm font-bold text-gray-900">{dymek?.tytul}</p>
        <p id="samouczek-dymek-tekst" className="mt-1 text-sm leading-relaxed text-gray-600">{dymek?.tekst}</p>
        <div className="mt-3 flex justify-end">
          <button ref={przycisk} type="button" onClick={zamykaj}
                  className="rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-blue-700">
            Rozumiem
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default Podswietlenie
